import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { colors, fontSize, margins } from "style";
import { useNavigate } from "react-router";
import { MUTATION_TYPE, useLinks, useLogin, useOtpChallenge } from "hooks";
import { css } from "@emotion/react";
import { VERIFY_OTP } from "graphql/mutations";
import { OTP_LENGTH } from "constants/index";
import { formatCountDown } from "utils";
import { PrimaryLink } from "components/Links";
import { Col } from "components/Containers";
import Form, { SIZE, TYPE } from "components/Form";

/**
 * OTPForm
 */
const OTPForm = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { handleLogin } = useLogin();
  const { challengeToken, secondsUntilResend, canResend, handleResendOtp, resending } = useOtpChallenge();
  const links = useLinks();
  const formRef = useRef();
  const [otp, setOtp] = useState();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const buttonLabel = isSigningIn
    ? "Login.LoginForm.SigningIn.Button"
    : isVerifying
    ? "Login.LoginForm.Submitting.Button"
    : "Login.LoginForm.Button";

  useEffect(() => {
    if (!challengeToken) {
      navigate(links.login);
    }
  }, [challengeToken]);

  useEffect(() => {
    if (otp?.length === OTP_LENGTH) {
      formRef.current?.submit();
    }
  }, [otp]);

  const handleComplete = (data) => {
    setIsSigningIn(true);

    const credentials = data?.verifyOtp?.credentials;
    const redirectTo = data?.verifyOtp?.redirectTo;

    handleLogin({ credentials, redirectTo });
  };

  return (
    <Col>
      <span css={styles.title}>
        <FormattedMessage id="OTP.OTPForm.Title" />
      </span>
      <span css={styles.subtitle}>
        <FormattedMessage id="OTP.OTPForm.Subtitle" />
      </span>
      <Form
        ref={formRef}
        data={getFormData(setOtp)}
        onLoadingState={setIsVerifying}
        saveButton={{
          show: true,
          labelId: intl.formatMessage({ id: buttonLabel }),
          disabled: isVerifying || isSigningIn,
          props: {
            css: styles.button,
          },
        }}
        mutationData={{
          mutationName: {
            add: VERIFY_OTP,
          },
          unWrapParams: true,
        }}
        initialValues={{
          challengeToken: challengeToken,
        }}
        mutationType={MUTATION_TYPE.add}
        marginSize={margins.none}
        onCompleted={handleComplete}
      />
      <span css={styles.resend_otp}>
        {canResend ? (
          <FormattedMessage
            id="OTP.OTPForm.ResendOTP.Link.Text"
            values={{
              link: (
                <PrimaryLink onClick={() => handleResendOtp(challengeToken)} disabled={resending}>
                  <FormattedMessage id="OTP.OTPForm.ResendOTP.Link" />
                </PrimaryLink>
              ),
            }}
          />
        ) : (
          <FormattedMessage
            id="OTP.OTPForm.ResendOTP"
            values={{
              time: formatCountDown(secondsUntilResend),
            }}
          />
        )}
      </span>
      <span css={styles.resend_otp}>
        <FormattedMessage
          id="OTP.OTPForm.ResendOTP.Login"
          values={{
            link: (
              <PrimaryLink href={links.login} routerEnabled>
                <FormattedMessage id="Login.LoginForm.Title" />
              </PrimaryLink>
            ),
          }}
        />
      </span>
    </Col>
  );
};

const styles = {
  title: css`
    color: ${colors.black};
    font-size: ${fontSize.xxlarge};
    margin-bottom: 1.5rem;
    font-weight: 700;
  `,
  subtitle: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyLight1};
    margin-bottom: 3rem;
    font-weight: 500;
  `,
  button: css`
    width: 100%;
  `,
  form_container: css`
    width: 100%;
  `,
  resend_otp: css`
    font-size: ${fontSize.small};
    color: ${colors.grayAnatomyLight1};
    margin: 3rem auto 0 auto;
  `,
};

const getFormData = (setOtp) => [
  {
    style: styles.form_container,
    items: [
      {
        type: TYPE.otp,
        size: SIZE.xlarge,
        onChange: (name, value, handleOnChange) => {
          handleOnChange(name, value);
          setOtp(value);
        },
        properties: {
          name: "otp",
          placeholder: "Enter OTP",
          required: true,
          autoFocus: true,
          maxLength: OTP_LENGTH,
          validate: (value) => value?.length === OTP_LENGTH,
        },
      },
      {
        type: TYPE.hidden,
        properties: {
          name: "challengeToken",
        },
      },
    ],
  },
];

export default OTPForm;
